/**
 * The ORSEE API answers 401 when the participant token is missing, invalid or
 * expired. Participant tokens live 60 minutes, and since the API security update
 * (ORSEE docs/security-api-auth.md A2) an expired token is really refused, so the
 * app must end the session instead of failing call by call.
 *
 * On a 401 while a token is stored (i.e. the user was logged in), clear the
 * session once and send the user to the landing page with ?session=expired.
 * The login call itself is excluded: a wrong password is not an expired session.
 */
export const SESSION_EXPIRED_PARAM = "session";
export const SESSION_EXPIRED_VALUE = "expired";

const landingUrl = () => {
  const prefix = process.env.REACT_APP_PREFIX || "";
  return `/${prefix}?${SESSION_EXPIRED_PARAM}=${SESSION_EXPIRED_VALUE}`;
};

// Tear the session down in one place: the persisted user slice would otherwise
// outlive the token and the app would reload still believing it is logged in.
// The store is imported lazily: store -> slices -> thunks -> this module.
const clearSession = async () => {
  localStorage.removeItem("jwtToken");
  try {
    const [{ default: store, persistor }, { logout }] = await Promise.all([
      import("../store"),
      import("../store/slice/userSlice"),
    ]);
    store.dispatch(logout());
    await persistor.flush();
  } catch {
    localStorage.removeItem("persist:user");
  }
};

const handleExpiredSession = (
  instance,
  { redirect = (url) => window.location.assign(url), onExpired = clearSession } = {}
) => {
  let handling = false;
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const wasLoggedIn = !!localStorage.getItem("jwtToken");
      if (status === 401 && wasLoggedIn && !url.includes("auth/login") && !handling) {
        handling = true;
        await onExpired();
        redirect(landingUrl());
      }
      return Promise.reject(error);
    }
  );
  return instance;
};

export default handleExpiredSession;
